import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPortalDropdownOpen, setIsPortalDropdownOpen] = useState(false);
  const location = useLocation();

  type MenuItem = {
    path: string;
    label: string;
    external?: boolean;
  };

  const menuItems: MenuItem[] = [
    { path: '/', label: 'Home' },
    { path: '/about-us', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/branches', label: 'Branches' },
    { path: '/contact-us', label: 'Contact Us' },
    { path: '/pre-registration', label: 'Pre-registration' },
  ];

  const portalItems = [
    // { path: 'https://pdsc.setupphils.com/login/agency', label: 'Agency Portal', external: true },
    { path: '/user/login', label: 'User' },
    { path: '/applicant/login', label: 'Applicant' }
  ];

  const handleMenuClick = () => {
    setIsMobileMenuOpen(false);
    setIsPortalDropdownOpen(false);
  };

  return (
    <>
      {/* Desktop Header - Single Row Navigation */}
      <header className="hidden lg:block bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            {/* Navigation Menu */}
            <nav className="flex items-center space-x-1">
              {menuItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <React.Fragment key={item.path}>
                    {index > 0 && <div className="w-px h-4 bg-gray-300 mx-4"></div>}
                    {item.external ? (
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium transition-colors duration-200 hover:text-blue-600 px-3 py-2 text-gray-700"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.path}
                        className={`text-sm font-medium transition-colors duration-200 hover:text-blue-600 px-3 py-2 flex items-center space-x-2 ${isActive ? 'text-blue-600' : 'text-gray-700'
                          }`}
                      >
                        {item.path === '/' && (
                          <img
                            src="/img/logos/pds_logo.svg"
                            alt="PDSC Logo"
                            className="w-6 h-6"
                          />
                        )}
                        <span>{item.label}</span>
                      </Link>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Portal Dropdown */}
              <div className="w-px h-4 bg-gray-300 mx-4"></div>
              <div className="relative">
                <button
                  onClick={() => setIsPortalDropdownOpen(!isPortalDropdownOpen)}
                  className="text-sm font-medium transition-colors duration-200 hover:text-blue-600 px-3 py-2 text-gray-700 flex items-center space-x-1"
                >
                  <span>Portal</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isPortalDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isPortalDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                    {portalItems.map((item) => (
                      <React.Fragment key={item.path}>
                        {/* {item.external ? (
                          <a
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsPortalDropdownOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors duration-200"
                          >
                            {item.label}
                          </a>
                        ) : (
                          <Link
                            to={item.path}
                            onClick={() => setIsPortalDropdownOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors duration-200"
                          >
                            {item.label}
                          </Link>
                        )} */
                        (
                          <Link
                            to={item.path}
                            onClick={() => setIsPortalDropdownOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors duration-200"
                          >
                            {item.label}
                          </Link>
                        )
                        }
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>

        {/* Backdrop for dropdown */}
        {isPortalDropdownOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsPortalDropdownOpen(false)}
          ></div>
        )}
      </header>

      {/* Mobile Header - Clean and Simple */}
      <header className="lg:hidden bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img
                src="/img/logos/pds_logo.svg"
                alt="PDSC Logo"
                className="w-8 h-8"
              />
              <span className="text-lg font-bold text-gray-800">PDSC</span>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="bg-white border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {menuItems.map((item) => (
                <React.Fragment key={item.path}>
                  {item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleMenuClick}
                      className="block w-full text-left px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 text-gray-700 hover:text-blue-600 hover:bg-gray-100"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={handleMenuClick}
                      className={`block w-full text-left px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 flex items-center space-x-2 ${location.pathname === item.path
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                        }`}
                    >
                      <span>{item.label}</span>
                    </Link>
                  )}
                </React.Fragment>
              ))}

              {/* Mobile Portal Section */}
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Portal
                </div>
                {portalItems.map((item) => (
                  <React.Fragment key={item.path}>
                    {/* {item.external ? (
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleMenuClick}
                        className="block w-full text-left px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 text-gray-700 hover:text-blue-600 hover:bg-gray-100 ml-4"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={handleMenuClick}
                        className={`block w-full text-left px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 ml-4 ${location.pathname === item.path
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                          }`}
                      >
                        <span>{item.label}</span>
                      </Link>
                    )} */
                    
                    (
                      <Link
                        to={item.path}
                        onClick={handleMenuClick}
                        className={`block w-full text-left px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 ml-4 ${location.pathname === item.path
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                          }`}
                      >
                        <span>{item.label}</span>
                      </Link>
                    )
                    }
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;